// Front-end mirror of backend calculate_lines() — grouped by room + multiplier + show_unit_prices.
// VAT rule: project.default_vat_rate is the SINGLE SOURCE OF TRUTH.
// Per-line vat_rate_override and price_code.vat_rate are IGNORED at calculation time
// (kept in DB for backward compat / future advanced override).

import { computeJoineryObjectQty as _computeJoineryObjectQty } from "./autoSource";

// v6 C5 — compute (gross, deductions, net) for a WorkZone based on calc_mode.
//   rect          : gross = L × H
//   triangle      : gross = (L × H) / 2
//   rect_triangle : gross = L × H1 + (L × H2) / 2
//   manual        : gross = zone.area  (legacy)
// Deductions: sum of (L × H) per row. Net = max(0, gross - deductions).
export function computeZoneArea(zone) {
  if (!zone) return { gross: 0, deduction: 0, net: 0 };

  // v6 F42 — Gevelwerken v2 multi-surface flow. Als de zone gevelvlakken
  // heeft → itereer daarover. Anders val terug op legacy calc_mode.
  const surfaces = zone.facade_surfaces || [];
  if (surfaces.length > 0) {
    let totalGross = 0;
    let totalDedu = 0;
    for (const s of surfaces) {
      totalGross += surfaceGross(s);
      totalDedu += (s.deductions || []).reduce((sum, d) => {
        const a = (+d.length || 0) * (+d.height || 0);
        return a > 0 ? sum + a : sum;
      }, 0);
    }
    const totalNet = Math.max(0, totalGross - totalDedu);
    return { gross: round2(totalGross), deduction: round2(totalDedu), net: round2(totalNet) };
  }

  // Legacy single-surface flow (unchanged).
  const mode = zone.calc_mode || "manual";
  const L = +zone.dim_length || 0;
  const H1 = +zone.dim_height || 0;
  const H2 = +zone.dim_height2 || 0;
  let gross = 0;
  if (mode === "rect")          gross = L * H1;
  else if (mode === "triangle") gross = (L * H1) / 2;
  else if (mode === "rect_triangle") gross = (L * H1) + ((L * H2) / 2);
  else gross = +zone.area || 0; // manual (legacy)
  const dedu = (zone.deductions || []).reduce((s, d) => {
    const a = (+d.length || 0) * (+d.height || 0);
    return a > 0 ? s + a : s;
  }, 0);
  const net = Math.max(0, gross - dedu);
  return { gross: round2(gross), deduction: round2(dedu), net: round2(net) };
}

// v6 F42 — bruto m² voor één FacadeSurface (front-end mirror van backend).
export function surfaceGross(surface) {
  if (!surface) return 0;
  const shape = (surface.shape_type || "rect").toLowerCase();
  if (shape === "manual") return +surface.manual_area || 0;
  const L = +surface.dim_length || 0;
  const H1 = +surface.dim_height || 0;
  if (shape === "triangle")  return (L * H1) / 2;
  if (shape === "trapezoid") {
    const H2 = +surface.dim_height2 || 0;
    return L * ((H1 + H2) / 2);
  }
  return L * H1; // rect default
}

// v6 F42 — netto m² voor één FacadeSurface (bruto minus per-surface aftrekken).
export function surfaceNet(surface) {
  const gross = surfaceGross(surface);
  const dedu = (surface?.deductions || []).reduce((s, d) => {
    const a = (+d.length || 0) * (+d.height || 0);
    return a > 0 ? s + a : s;
  }, 0);
  return { gross: round2(gross), deduction: round2(dedu), net: round2(Math.max(0, gross - dedu)) };
}

export function measurementQty(m, zonesById = null, roomsById = null, autoSourcesCtx = null) {
  // v6 F16 S4 — Auto-source override (Trap-model gegeneraliseerd).
  // Als m.auto_source gezet is, wint dat over de formule/L/W/H/Q.
  // `autoSourcesCtx` = { autoSources, partsDefaults, resolveAutoSource } —
  // wordt via calcLocal doorgegeven. Resolver-callback voorkomt circulaire
  // import tussen calculator.js en autoSource.js.
  // v14at-P10-C — Dirty-flag precedence: resolver zwijgt wanneer de user
  // de hoeveelheid handmatig heeft aangepast (qty_user_override=true).
  if (m.auto_source && zonesById && autoSourcesCtx?.resolveAutoSource && !m.qty_user_override) {
    const zone = zonesById[m.zone_id];
    if (zone) {
      const resolved = autoSourcesCtx.resolveAutoSource(
        m.auto_source, zone,
        autoSourcesCtx.autoSources || {},
        autoSourcesCtx.partsDefaults || {}
      );
      const mult = Math.max(1, +m.multiplier || 1);
      return {
        gross: round2(resolved * mult),
        deduction: 0,
        net_unit: round2(resolved),
        net: round2(resolved * mult),
        multiplier: mult,
      };
    }
  }
  const f = m.formula || "wall";
  const L = +m.length || 0;
  const W = +m.width || 0;
  const H = +m.height || 0;
  const Q = +m.quantity || 0;
  let gross = 0, dedu = 0, net = 0;
  if (f === "wall") {
    gross = L * H;
    dedu = (m.deductions || []).reduce(
      (s, d) => (((+d.area || 0) >= 1.0) ? s + (+d.area || 0) : s), 0
    );
    net = Math.max(0, gross - dedu);
  } else if (f === "triangle") {
    // v6 C4 — driehoek topgevel: (L × H) / 2
    gross = (L * H) / 2;
    dedu = (m.deductions || []).reduce(
      (s, d) => (((+d.area || 0) >= 1.0) ? s + (+d.area || 0) : s), 0
    );
    net = Math.max(0, gross - dedu);
  } else if (f === "zone_net") {
    // v6 C5 — qty inherited from parent WorkZone's net area
    const z = (zonesById || {})[m.zone_id];
    if (z) {
      const { net: znet } = computeZoneArea(z);
      gross = znet; dedu = 0; net = znet;
    } else {
      gross = Q; net = Q;
    }
  } else if (f === "extra_element_qty") {
    // v6 F42j — Fase 2c: qty komt van FacadeExtraElement.qty binnen dezelfde
    // zone. Geen coats/multiplier van geveloppervlakte.
    const z = (zonesById || {})[m.zone_id];
    let val = Q;
    if (z && m.extra_element_id) {
      const el = (z.facade_extra_elements || []).find((e) => e.id === m.extra_element_id);
      if (el) val = +el.qty || 0;
    }
    gross = val; net = val;
  } else if (f === "floor" || f === "ceiling") {
    // v6 F11.5 — Plafond/vloer-kwantiteit moet exact dezelfde fallback-logica
    // volgen als backend `_measurement_quantity` (server.py F10.2/F10.3), anders
    // ontstaat preview ↔ PDF mismatch in kamer-subtotalen:
    //   1. Free-mode kamer → vertrouw `quantity` (som van ceiling_segments)
    //      ongeacht of L×W ook ingevuld is.
    //   2. Anders → L×W (snelle opmeting).
    //   3. Fallback (legacy / segments-only): als L×W=0 maar Q>0, gebruik Q.
    const room = m.room_id && roomsById ? roomsById[m.room_id] : null;
    const isFreeSurface = !!room && room.measure_method === "free" && Q > 0;
    if (isFreeSurface) {
      gross = Q;
    } else {
      gross = L * W;
      if (gross === 0 && Q > 0) gross = Q;
    }
    net = gross;
  } else if (f === "linear") {
    gross = L; net = L;
  } else if (f === "piece" || f === "hour" || f === "custom") {
    gross = Q; net = Q;
  } else if (f === "fixed") {
    // v6 F11.5 — Spiegelt backend `_measurement_quantity` voor fixed:
    // respecteer stored quantity, met fallback 1 bij Q=0 (legacy / default).
    gross = Q > 0 ? Q : 1; net = gross;
  }
  if (m.multiplier_coats && (f === "wall" || f === "floor" || f === "ceiling" || f === "triangle" || f === "zone_net")) {
    const c = Math.max(1, Math.min(3, +m.coats || 2));
    net *= c;
  }
  const mult = Math.max(1, +m.multiplier || 1);
  return {
    gross: round2(gross * mult),
    deduction: round2(dedu * mult),
    net_unit: round2(net),
    net: round2(net * mult),
    multiplier: mult,
  };
}

const FORMULA_UNIT = {
  wall: "m2", floor: "m2", ceiling: "m2", triangle: "m2", zone_net: "m2",
  linear: "lm", piece: "piece", hour: "hour",
  fixed: "fixed", custom: "piece",
};

export function calcLocal(quote, priceCodesIndex, projectVatRate, autoSourcesCtx = null) {
  const lines = [];
  // Single source of truth: project VAT rate (fallback 21 only if project missing)
  const projectVat = projectVatRate != null ? +projectVatRate : 21;
  const vatBuckets = { 0: 0, 6: 0, 21: 0 };
  let subtotal = 0;
  // v6 C5 — index zones for zone_net qty lookup
  const zonesById = {};
  for (const z of quote.work_zones || []) { if (z?.id) zonesById[z.id] = z; }
  // v6 F11.5 — index rooms so measurementQty kan free-mode fallback toepassen
  // voor plafond/vloer (mirror van backend `_measurement_quantity`).
  const roomsById = {};
  for (const r of quote.rooms || []) { if (r?.id) roomsById[r.id] = r; }

  for (const m of quote.measurements || []) {
    const { gross, deduction, net, multiplier } = measurementQty(m, zonesById, roomsById, autoSourcesCtx);
    const pc = m.price_code_id ? priceCodesIndex[m.price_code_id] : null;
    const unit = pc?.unit || FORMULA_UNIT[m.formula || "wall"];
    // v6 F42k v14 P0-CRITICAL — Spiegel van backend calc-precedence:
    //   1. unit_price_override (bewuste user-override)
    //   2. unit_price_snapshot (historische prijs bij aanmaak)
    //   3. pc.default_price (legacy fallback)
    const unit_price =
      m.unit_price_override != null ? +m.unit_price_override
        : m.unit_price_snapshot != null ? +m.unit_price_snapshot
        : pc ? +pc.default_price : 0;
    const vat_rate = projectVat;
    const code = pc?.code || "—";
    // v6 F42k v14b (final polish) — Description-precedence gelijk aan backend:
    //   description_override → description_snapshot → pc.description → m.description → ""
    const desc = m.description_override || m.description_snapshot || pc?.description || m.description || "";    const total_excl = round2(net * unit_price);
    const vat_amount = round2((total_excl * vat_rate) / 100);
    subtotal += total_excl;
    vatBuckets[vat_rate] = (vatBuckets[vat_rate] || 0) + vat_amount;
    lines.push({
      code, description: desc, quantity: net, gross, deduction, multiplier,
      unit, unit_price, vat_rate, total_excl, vat_amount,
      total_incl: round2(total_excl + vat_amount), kind: "measurement",
      item_id: m.id, room_id: m.room_id || null, zone_id: m.zone_id || null,
    });
  }

  for (const el of quote.extra_lines || []) {
    const qty = +el.quantity || 0;
    // v6 F42k v14 — ExtraLine unit_price is user-owned. Fallback naar snapshot
    // en tenslotte pc.default_price (voor consistent Editor=Preview gedrag).
    const pcEl = el.price_code_id ? priceCodesIndex[el.price_code_id] : null;
    const unit_price = (+el.unit_price || 0) > 0
      ? +el.unit_price
      : (el.unit_price_snapshot != null ? +el.unit_price_snapshot
         : (pcEl ? +pcEl.default_price : 0));
    // v6 F42k v14c — ExtraLine mag eigen vat_rate hebben (spiegel van backend
    // r4254: int(el.vat_rate) if el.vat_rate is not None else project_vat).
    // Voorheen forceerde de FE altijd projectVat → BTW-buckets divergeerden
    // van de backend voor extra_lines met een eigen (bv. 21%) vat_rate.
    const vat_rate = (el.vat_rate !== null && el.vat_rate !== undefined && el.vat_rate !== "")
      ? +el.vat_rate
      : projectVat;
    const total_excl = round2(qty * unit_price);
    const vat_amount = round2((total_excl * vat_rate) / 100);
    subtotal += total_excl;
    vatBuckets[vat_rate] = (vatBuckets[vat_rate] || 0) + vat_amount;
    lines.push({
      code: "EXTRA",
      // v6 F42k v14b (final polish) — Description precedence spiegel:
      // override → snapshot → description → pc.description → ""
      description: el.description_override || el.description_snapshot || el.description || (pcEl?.description) || "",
      quantity: qty,
      gross: qty, deduction: 0, multiplier: 1, unit: el.unit || "fixed",
      unit_price, vat_rate, total_excl, vat_amount,
      total_incl: round2(total_excl + vat_amount), kind: "extra",
      item_id: el.id, room_id: el.room_id || null, zone_id: el.zone_id || null,
    });
  }

  // v6 F11.6a — Joinery objects → calc-lines (mirror van backend calculate_lines).
  // v6 F22 — Ondersteunt nu ook v15 zone_types (interior_joinery, exterior_joinery)
  // via zw.auto_source + auto_source_multiplier snapshot uit template-regel.
  for (const z of quote.work_zones || []) {
    const zt = z.zone_type;
    const isV15 = zt === "interior_joinery" || zt === "exterior_joinery";
    if (zt !== "joinery" && !isV15) continue;
    const objects = z.joinery_objects || [];

    // ── (A) v15 zones met auto_source-gebaseerde template-regels ──
    if (isV15) {
      for (const zw of (z.joinery_works || [])) {
        const filterIds = zw.object_ids;
        const applicable = objects.filter((o) => !filterIds || filterIds.includes(o.id));
        if (applicable.length === 0) continue;
        const pc = zw.price_code_id ? priceCodesIndex[zw.price_code_id] : null;
        // v6 F22 — Snapshot uit template-regel op de work; als niet aanwezig
        // → fallback op zone-objecthoeveelheid (spiegel van server.py r4364-4383).
        // v6 F23 — Forfait/fixed price_codes zonder auto_source → qty=1.
        // v6 F42k v14d — Custom werken zonder auto_source met unit lm/m2/piece
        // vielen voorheen op qty=0 → editor ≠ backend. Nu spiegelen we het
        // backend-pad: aggregate compute_joinery_object_quantities over de
        // applicable objecten, kies bucket via pc.unit.
        const src = zw.auto_source;
        const mult = +zw.auto_source_multiplier || 1;
        let qty = 0;
        if (src && autoSourcesCtx?.resolveAutoSource) {
          const resolved = autoSourcesCtx.resolveAutoSource(
            src, z,
            autoSourcesCtx.autoSources || {},
            autoSourcesCtx.partsDefaults || {}
          );
          qty = Math.max(0, resolved * mult);
        } else if (!src && pc && pc.unit === "fixed") {
          qty = 1;
        } else if (!src && pc) {
          // v14d: no auto_source, not fixed → aggregate over objects
          const pDefaults = autoSourcesCtx?.partsDefaults || {};
          let totalArea = 0, totalLm = 0, totalPieces = 0;
          for (const obj of applicable) {
            const q = _computeJoineryObjectQty(obj, pDefaults);
            totalArea += q.area_m2;
            totalLm += q.length_lm;
            totalPieces += q.pieces;
          }
          if (pc.unit === "m2") qty = totalArea;
          else if (pc.unit === "lm" || pc.unit === "m") qty = totalLm;
          else if (pc.unit === "piece") qty = totalPieces;
          else qty = 0;
        }
        if (qty <= 0) continue;
        // v6 F42k v14 — Precedence spiegel voor joinery_works
        const unit_price = zw.unit_price && +zw.unit_price !== 0
          ? +zw.unit_price
          : (zw.unit_price_snapshot != null ? +zw.unit_price_snapshot
             : (pc ? +pc.default_price : 0));
        const qtyR = round2(qty);
        const total_excl = round2(qtyR * unit_price);
        const vat_amount = round2((total_excl * projectVat) / 100);
        const workLabel = _joineryWorkLabel(zw.work_kind);
        // v6 F42k v14b (final polish) — Description precedence spiegel (v15 auto_source pad)
        const baseDesc = zw.description_override || zw.description_snapshot || zw.description || (pc?.description) || workLabel;
        const kindCounts = {};
        for (const obj of applicable) {
          const c = Math.max(1, Math.round(+obj.count || 1));
          kindCounts[obj.kind || "custom_object"] = (kindCounts[obj.kind || "custom_object"] || 0) + c;
        }
        const objDesc = Object.entries(kindCounts).map(([k, c]) => {
          const lbl = _joineryKindLabel(k).toLowerCase();
          const plural = c > 1 && !lbl.endsWith("s") ? "s" : "";
          return `${c} ${lbl}${plural}`;
        }).join(", ");
        const desc = objDesc ? `${baseDesc} — ${objDesc}` : baseDesc;
        subtotal += total_excl;
        vatBuckets[projectVat] = (vatBuckets[projectVat] || 0) + vat_amount;
        lines.push({
          code: pc ? pc.code : "SCHR",
          description: desc,
          quantity: qtyR,
          gross: qtyR,
          deduction: 0,
          multiplier: 1,
          unit: pc?.unit || "m2",
          unit_price,
          vat_rate: projectVat,
          total_excl,
          vat_amount,
          total_incl: round2(total_excl + vat_amount),
          kind: "joinery",
          room_id: null,
          zone_id: z.id,
          item_id: `zw:${zw.id}:v15`,
          joinery_work_id: zw.id,
          template_line_id: zw.template_line_id || null,
        });
      }
      continue;
    }

    // ── (1) Zone-level werken ──
    // v6 F11.6a-final — meetmethode is nu zone-globaal. Fallback naar
    // object.measure_mode voor backwards-compat.
    const zoneMode = z.joinery_measure_mode;
    for (const zw of (z.joinery_works || [])) {
      const filterIds = zw.object_ids;
      const applicable = objects.filter((o) => !filterIds || filterIds.includes(o.id));
      if (applicable.length === 0) continue;

      const buckets = { per_piece: 0, by_dimension: 0, forfait: 0 };
      const kindCounts = {};
      for (const obj of applicable) {
        const count = Math.max(1, Math.round(+obj.count || 1));
        const sideMult = (obj.side || "exterior") === "both" ? 2.0 : 1.0;
        const mode = zoneMode || obj.measure_mode || "per_piece";
        if (mode === "by_dimension") {
          const base = Math.max(0, (+obj.length || 0) * (+obj.height || 0));
          buckets.by_dimension += base * count * sideMult;
        } else if (mode === "forfait") {
          buckets.forfait += (+obj.forfait_amount || 0) * count * sideMult;
        } else {
          buckets.per_piece += count * sideMult;
        }
        kindCounts[obj.kind || "custom_object"] =
          (kindCounts[obj.kind || "custom_object"] || 0) + count;
      }

      const workLabel = _joineryWorkLabel(zw.work_kind);
      // v6 F42k v14b (final polish) — Description precedence spiegel (legacy bucket pad)
      const baseDesc = zw.description_override || zw.description_snapshot || zw.description || (pc?.description) || workLabel;
      const objDesc = Object.entries(kindCounts).map(([k, c]) => {
        const lbl = _joineryKindLabel(k).toLowerCase();
        const plural = c > 1 && !lbl.endsWith("s") ? "s" : "";
        return `${c} ${lbl}${plural}`;
      }).join(", ");
      const pc = zw.price_code_id ? priceCodesIndex[zw.price_code_id] : null;

      for (const [bucketMode, totalQty] of Object.entries(buckets)) {
        if (totalQty <= 0) continue;
        let unit_price, qty_for_line, line_total, unit_label;
        if (bucketMode === "forfait") {
          line_total = round2(totalQty);
          unit_label = "fixed";
          unit_price = 0;
          qty_for_line = 1;
        } else {
          // v6 F42k v14 — Precedence spiegel: user zw.unit_price → snapshot → pc.default_price
          unit_price = zw.unit_price && +zw.unit_price !== 0
            ? +zw.unit_price
            : (zw.unit_price_snapshot != null ? +zw.unit_price_snapshot
               : (pc ? +pc.default_price : 0));
          qty_for_line = round2(totalQty);
          line_total = round2(qty_for_line * unit_price);
          unit_label = bucketMode === "by_dimension" ? "m2" : "piece";
        }
        const desc = objDesc ? `${baseDesc} — ${objDesc}` : baseDesc;
        const vat_amount = round2((line_total * projectVat) / 100);
        subtotal += line_total;
        vatBuckets[projectVat] = (vatBuckets[projectVat] || 0) + vat_amount;
        lines.push({
          code: pc ? pc.code : "SCHR",
          description: desc,
          quantity: qty_for_line,
          gross: qty_for_line,
          deduction: 0,
          multiplier: 1,
          unit: unit_label,
          unit_price,
          vat_rate: projectVat,
          total_excl: line_total,
          vat_amount,
          total_incl: round2(line_total + vat_amount),
          kind: "joinery",
          room_id: null,
          zone_id: z.id,
          item_id: `zw:${zw.id}:${bucketMode}`,
          joinery_work_id: zw.id,
          joinery_measure_mode: bucketMode,
        });
      }
    }

    // ── (2) Per-object werken (legacy) ──
    for (const obj of objects) {
      const objWorkLines = obj.work_lines || [];
      if (objWorkLines.length === 0) continue;
      const count = Math.max(1, Math.round(+obj.count || 1));
      const length = +obj.length || 0;
      const height = +obj.height || 0;
      const forfaitAmt = +obj.forfait_amount || 0;
      const objSide = obj.side || "exterior";
      const objName = obj.name || _joineryKindLabel(obj.kind);

      for (const wl of objWorkLines) {
        const wlSide = wl.side || objSide;
        const sideMult = wlSide === "both" ? 2.0 : 1.0;
        let qty = 0, unitLabel = "piece";
        if (obj.measure_mode === "by_dimension") {
          qty = round2(Math.max(0, length * height) * count);
          unitLabel = "m2";
        } else if (obj.measure_mode === "forfait") {
          qty = count;
          unitLabel = "fixed";
        } else {
          qty = count;
          unitLabel = "piece";
        }
        const pc = wl.price_code_id ? priceCodesIndex[wl.price_code_id] : null;
        let unit_price;
        if (obj.measure_mode === "forfait") {
          unit_price = forfaitAmt;
        } else {
          // v6 F42k v14 — Per-object work_lines: user wl.unit_price → snapshot → pc.default_price
          unit_price = wl.unit_price && +wl.unit_price !== 0
            ? +wl.unit_price
            : (wl.unit_price_snapshot != null ? +wl.unit_price_snapshot
               : (pc ? +pc.default_price : 0));
        }
        const workLabel = _joineryWorkLabel(wl.work_kind);
        // v6 F42k v14b (final polish) — Description precedence spiegel (per-object work_lines)
        const baseDesc = wl.description_override || wl.description_snapshot || wl.description || (pc?.description) || workLabel;
        let sideTag = "";
        if (wlSide === "interior") sideTag = " (binnen)";
        else if (wlSide === "exterior") sideTag = " (buiten)";
        else if (wlSide === "both") sideTag = " (binnen + buiten)";
        let desc = `${objName} — ${baseDesc}${sideTag}`;
        if (obj.measure_mode === "by_dimension" && length > 0 && height > 0) {
          desc += `  [${length.toFixed(2)} × ${height.toFixed(2)} m]`;
        }
        const total_excl = round2(qty * unit_price * sideMult);
        const vat_amount = round2((total_excl * projectVat) / 100);
        subtotal += total_excl;
        vatBuckets[projectVat] = (vatBuckets[projectVat] || 0) + vat_amount;
        lines.push({
          code: pc ? pc.code : "SCHR",
          description: desc,
          quantity: round2(qty * sideMult),
          gross: round2(qty * sideMult),
          deduction: 0,
          multiplier: sideMult,
          unit: unitLabel,
          unit_price,
          vat_rate: projectVat,
          total_excl,
          vat_amount,
          total_incl: round2(total_excl + vat_amount),
          kind: "joinery",
          room_id: null,
          zone_id: z.id,
          item_id: `${obj.id}::${wl.id}`,
          joinery_object_id: obj.id,
          joinery_kind: obj.kind,
          joinery_parent_id: obj.parent_object_id || null,
        });
      }
    }
  }

  const total_vat = round2(Object.values(vatBuckets).reduce((a, b) => a + b, 0));

  // Rooms summary
  const rooms_summary = [];
  const rooms = quote.rooms || [];
  const roomIds = rooms.map((r) => r.id).concat([null]);
  for (const rid of roomIds) {
    const rlines = lines.filter((ln) => (ln.room_id || null) === rid && !ln.zone_id);
    if (rlines.length === 0 && rid !== null) continue;
    if (rlines.length === 0) continue;
    const room = rid ? rooms.find((r) => r.id === rid) : null;
    const walls_gross = rlines
      .filter((ln) => ln.kind === "measurement" && ln.unit === "m2")
      .reduce((s, ln) => s + ln.gross, 0);
    const walls_dedu = rlines
      .filter((ln) => ln.kind === "measurement" && ln.unit === "m2")
      .reduce((s, ln) => s + ln.deduction, 0);
    rooms_summary.push({
      id: rid,
      name: room?.name || "Algemeen",
      walls_gross: round2(walls_gross),
      walls_deduction: round2(walls_dedu),
      walls_net: round2(walls_gross - walls_dedu),
      subtotal_excl: round2(rlines.reduce((s, ln) => s + ln.total_excl, 0)),
      lines: rlines,
    });
  }
  // v6 Fase C — work_zones summary (parallel to rooms)
  for (const z of quote.work_zones || []) {
    const zlines = lines.filter((ln) => ln.zone_id === z.id);
    if (zlines.length === 0) continue;
    const walls_gross = zlines
      .filter((ln) => ln.kind === "measurement" && ln.unit === "m2")
      .reduce((s, ln) => s + ln.gross, 0);
    const walls_dedu = zlines
      .filter((ln) => ln.kind === "measurement" && ln.unit === "m2")
      .reduce((s, ln) => s + ln.deduction, 0);
    rooms_summary.push({
      id: z.id,
      name: z.name || "Werkzone",
      zone_type: z.zone_type,
      is_zone: true,
      walls_gross: round2(walls_gross),
      walls_deduction: round2(walls_dedu),
      walls_net: round2(walls_gross - walls_dedu),
      subtotal_excl: round2(zlines.reduce((s, ln) => s + ln.total_excl, 0)),
      lines: zlines,
    });
  }

  return {
    lines,
    subtotal_excl: round2(subtotal),
    vat_breakdown: vatBuckets,
    total_vat,
    total_incl: round2(subtotal + total_vat),
    rooms_summary,
  };
}

// v6 F8 — Belgische geldformaat overal consistent:
//   € 0,00 / € 12,50 / € 1.234,56 / € 12.345,67
// Punt = duizendtalseparator, komma = decimaal, altijd 2 decimalen,
// spatie tussen € en bedrag. Wrapt Intl.NumberFormat zodat 1 centrale
// plek garandeert dat alle bedragen identiek worden weergegeven.
const _BE_FORMATTER = new Intl.NumberFormat("nl-BE", {
  style: "currency",
  currency: "EUR",
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

export function formatCurrencyBE(amount) {
  // Intl in nl-BE geeft "€ 1.234,56" (met NBSP). Normaliseer naar gewone
  // spatie voor consistente weergave in tests, kopieerbaar in mailclients.
  return _BE_FORMATTER.format(+amount || 0).replace(/\u00A0/g, " ");
}

// Alias voor backwards compat — historisch in gebruik.
export const fmtEUR = formatCurrencyBE;

function round2(n) { return Math.round(n * 100) / 100; }

// v6 F11.6a — Joinery labels (gespiegeld met backend _JOINERY_*_LABEL)
const _JOINERY_KIND_LABEL = {
  window:        "Raam",
  door:          "Deur",
  garage_door:   "Garagepoort",
  gate:          "Poort / hek",
  shutter:       "Luik",
  shutter_box:   "Rolluikkast",
  shutter_slats: "Rolluiklamellen",
  custom_object: "Object",
};
const _JOINERY_WORK_LABEL = {
  degrease: "Ontvetten",
  sand:     "Schuren",
  fill:     "Plamuren",
  primer:   "Primer",
  paint:    "Lakken",
  varnish:  "Vernissen",
  oil:      "Oliën",
  stain:    "Beitsen",
  custom:   "Werk",
};
function _joineryKindLabel(k) { return _JOINERY_KIND_LABEL[k] || "Object"; }
function _joineryWorkLabel(w) { return _JOINERY_WORK_LABEL[w] || "Werk"; }

export const UNITS = ["m2", "lm", "piece", "hour", "fixed"];
export const UNIT_LABEL = { m2: "m²", lm: "lm", piece: "stuk", hour: "uur", fixed: "forfait" };
export const FORMULAS = [
  { value: "wall", label: "Wand (L × H)" },
  { value: "ceiling", label: "Plafond (L × B)" },
  { value: "floor", label: "Vloer (L × B)" },
  { value: "linear", label: "Lopende meter" },
  { value: "piece", label: "Stuks" },
  { value: "hour", label: "Uren" },
  { value: "fixed", label: "Forfait" },
  { value: "custom", label: "Manuele invoer" },
];

export const ROOM_PRESETS = [
  "Living", "Keuken", "Eetkamer", "Hal / Inkom", "Traphal",
  "Slaapkamer 1", "Slaapkamer 2", "Slaapkamer 3",
  "Badkamer", "Toilet", "Berging", "Bureau", "Garage", "Zolder",
];
